/**
 * Менеджер для управления временными маркерами на карте
 */

class MarkerManager {
    constructor (map) {
        this.map = map;
        this.markers = new Map(); // id -> {marker, coords}
        this.markerCount = 0;
        this.sourceId = 'temporary-markers-source';
        this.layerId = 'temporary-markers-layer';
        this.sourceAdded = false;
    }

    /**
     * Добавить маркер на карту
     * @param {Object} coords - {lat, lng}
     * @returns {string} - id маркера
     */
    addMarker (coords) {
        this.markerCount++;
        const id = `marker-${this.markerCount}`;

        // Убеждаемся, что источник существует
        this.ensureSourceAndLayer();

        // Добавляем маркер в Map
        this.markers.set(id, {
            coords,
            markerNumber: this.markerCount,
        });

        // Обновляем данные источника
        this.updateSource();

        return id;
    }

    /**
     * Удалить маркер с карты
     * @param {string} id - id маркера
     */
    removeMarker (id) {
        if (this.markers.has(id)) {
            this.markers.delete(id);
            this.updateSource();

            // Если маркеров нет, удаляем слой и источник
            if (this.markers.size === 0) {
                this.removeSourceAndLayer();
            }
        }
    }

    /**
     * Получить все маркеры
     */
    getMarkers () {
        return Array.from(this.markers.entries()).map(([ id, data ]) => ({
            id,
            ...data,
        }));
    }

    /**
     * Проверить есть ли маркер
     */
    hasMarkers () {
        return this.markers.size > 0;
    }

    /**
     * Очистить все маркеры
     */
    clearAllMarkers () {
        this.markers.clear();
        this.markerCount = 0;
        this.removeSourceAndLayer();
    }

    /**
     * Создать источник и слой если их нет
     */
    ensureSourceAndLayer () {
        if (this.sourceAdded) return;

        const source = this.map.getSource(this.sourceId);
        if (!source) {
            this.map.addSource(this.sourceId, {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: [],
                },
            });
        }

        const layer = this.map.getLayer(this.layerId);
        if (!layer) {
            this.map.addLayer({
                id: this.layerId,
                type: 'circle',
                source: this.sourceId,
                paint: {
                    'circle-radius': 6,
                    'circle-color': '#3b82f6',
                    'circle-opacity': 0.8,
                    'circle-stroke-width': 2,
                    'circle-stroke-color': '#ffffff',
                },
            });
        }

        // Добавляем слой для лейблов с номерами точек
        const labelLayer = this.map.getLayer(`${this.layerId}-label`);
        if (!labelLayer) {
            this.map.addLayer({
                id: `${this.layerId}-label`,
                type: 'symbol',
                source: this.sourceId,
                layout: {
                    'text-field': [ 'get', 'markerNumber' ],
                    'text-font': [ 'Open Sans Regular' ],
                    'text-size': 14,
                    'text-offset': [ 0.6, -0.6 ],
                    'text-anchor': 'bottom-left',
                },
                paint: {
                    'text-color': '#ffffff',
                },
            });
        }

        this.sourceAdded = true;
    }

    /**
     * Обновить данные источника GeoJSON
     */
    updateSource () {
        const source = this.map.getSource(this.sourceId);
        if (source) {
            const features = Array.from(this.markers.values()).map(data => ({
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [ data.coords.lng, data.coords.lat ],
                },
                properties: {
                    markerNumber: String(data.markerNumber),
                },
            }));

            source.setData({
                type: 'FeatureCollection',
                features,
            });
        }
    }

    /**
     * Удалить источник и слой
     */
    removeSourceAndLayer () {
        if (this.map.getLayer(`${this.layerId}-label`)) {
            this.map.removeLayer(`${this.layerId}-label`);
        }
        if (this.map.getLayer(this.layerId)) {
            this.map.removeLayer(this.layerId);
        }
        if (this.map.getSource(this.sourceId)) {
            this.map.removeSource(this.sourceId);
        }
        this.sourceAdded = false;
    }
}

export default MarkerManager;
